import React, { useState, useEffect, useRef } from "react";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { formatDate } from "../../../ListBlock/components/ListItem/helpers/formatDate";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { CgDetailsMore } from "react-icons/cg";
import { IListItem } from "../../../../interfaces/IDocDetails";
import styles from "../../../../styles/Markdown.module.css";
import { useTranslation } from "react-i18next";

interface ListItemProps {
  data: IListItem;
  isSelected: boolean;
  onSelect: (id: string, code: string) => void;
}

const FilteredItems: React.FC<ListItemProps> = ({
  data,
  onSelect,
  isSelected,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const titleRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (titleRef.current) {
      setIsTruncated(
        titleRef.current.scrollWidth > titleRef.current.clientWidth,
      );
    }
  }, [data.title]);

  const handleSelectDocuments = (case_no: string, code: string) => {
    onSelect(code, case_no);
  };

  const toggleExpandSummary = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="px-3 py-2 bg-base-color border border-slate-300 dark:border-slate-700 rounded-lg">
      <div className="text-lg text-del-light-theme-green dark:text-gray-300 font-bold flex justify-between items-center">
        <span className="flex items-center space-x-2 hover:underline dark:text-del-light-theme-green w-[93%]">
          <input
            type="checkbox"
            checked={isSelected}
            className="-ml-1 accent-del-light-theme-green dark:accent-del-green"
            onChange={() => handleSelectDocuments(data.case_no, data.code)}
          />
          <span
            ref={titleRef}
            onClick={() => handleSelectDocuments(data.case_no, data.code)}
            className={`truncate overflow-hidden text-ellipsis whitespace-nowrap tooltip-target tooltip-${data.id}`}
          >
            {data.title}
          </span>
          {isTruncated && (
            <Tooltip
              anchorSelect={`.tooltip-${data.id}`}
              place="top"
              positionStrategy="fixed"
              className="text-xs font-light z-50"
              content={data.title}
              style={{ maxWidth: "300px", whiteSpace: "normal" }}
            />
          )}
        </span>
        <a
          href={`/details/${data.code}/${data.case_no}`}
          target="_blank"
          rel="noopener noreferrer"
          className="-ml-1 p-2 rounded-full dark:hover:bg-gray-700 hover:bg-gray-200"
        >
          <CgDetailsMore
            className={`text-xl link-icon focus:outline-none tooltip-anchor-${data.id}`}
          />
        </a>
        <Tooltip
          anchorSelect={`.tooltip-anchor-${data.id}`}
          place="top"
          className="text-xs font-light z-50"
          content={t("tooltips.openOriginalDocument")}
        />
      </div>
      <div className="text-sm text-secondary-color">
        <span className="font-semibold">{t("docInfo.dateOfPublication")} </span>
        {formatDate(data.date)}
      </div>
      <div className="text-sm text-secondary-color">
        <span className="font-semibold">{t("docInfo.regulators")} </span>
        {!!data.regulator ? data.regulator : data.code}
      </div>
      <div className="text-sm text-secondary-color">
        <span className="font-semibold">{t("docInfo.class")} </span>
        {data.classes}
      </div>
      {data.short_summary && (
        <div className="flex items-start">
          <div
            className={`text-sm text-secondary-color ${
              isExpanded ? "" : `${styles["markdown-truncate"]}`
            }`}
          >
            <span className="font-bold">{t("docInfo.shortSummary")} </span>
            {data.short_summary ?? "-"}
          </div>

          <button
            onClick={toggleExpandSummary}
            className="-mr-2 text-black focus:outline-none dark:text-gray-400"
          >
            {isExpanded ? (
              <FiChevronUp size={20} />
            ) : (
              <FiChevronDown size={20} />
            )}
          </button>
        </div>
      )}
      {data.keywords.length > 0 && (
        <div className="flex flex-wrap mt-1 gap-y-1">
          {data?.keywords?.map(
            (keyword: string, index: number) =>
              keyword !== "" && (
                <span
                  key={index}
                  className="inline-flex items-center justify-center keyword-chip-color"
                >
                  {keyword}
                </span>
              ),
          )}
        </div>
      )}
      {data.tags.length > 0 && (
        <div className="flex flex-wrap mt-1 gap-y-1">
          {data?.tags?.map((tag: string, index: number) => (
            <span
              key={index}
              className="inline-flex items-center justify-center tag-chip-color"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      {data.highlights && (
        <>
          <hr className="my-1 border-gray-300 dark:border-gray-600" />
          <div className="text-sm text-base-color">{data.highlights}</div>
        </>
      )}
    </div>
  );
};

export { FilteredItems };
